import React, { useState } from 'react';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Maximize2 } from 'lucide-react';
import { Wall, generateWallDisplayName } from '@/utils/wallDetection';
import { ScaleData } from '@/types/floorPlanViewer';
import { formatMeasurement } from '@/utils/scaleCalculations';
import { toast } from 'sonner';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

interface ElevationCreationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roomId: string;
  roomName: string;
  wall: Wall | null;
  wallLength: number; // in real-world units
  scaleData: ScaleData | null;
  floorPlanId: string;
  onElevationCreated: (elevationId: string) => void;
}

export function ElevationCreationModal({
  open,
  onOpenChange,
  roomId,
  roomName,
  wall,
  wallLength,
  scaleData,
  floorPlanId,
  onElevationCreated
}: ElevationCreationModalProps) {
  // Get the unit from scale data - check for both 'ft' and 'in' as imperial units
  const isImperial = scaleData?.unit === 'ft' || scaleData?.unit === 'in';
  const unit = isImperial ? 'feet' : 'meters';
  const unitAbbr = scaleData?.unit || 'ft';
  
  // Set default height based on unit
  const defaultHeight = isImperial ? '10' : '3'; // 10 feet or 3 meters
  
  const [height, setHeight] = useState(defaultHeight);
  const [elevationName, setElevationName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const { userId: clerkUserId } = useClerkAuth();
  const navigate = useNavigate();
  
  // Generate default name
  const defaultName = wall ? generateWallDisplayName(wall, roomName) : '';
  const displayName = elevationName || defaultName;
  
  const handleCreate = async () => {
    if (!wall || !height || parseFloat(height) <= 0) {
      toast.error('Please enter a valid wall height');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const authSupabase = await getAuthenticatedSupabaseClient();
      
      // Create the elevation record
      const { data: elevation, error } = await authSupabase
        .from('elevations')
        .insert({
          floor_plan_id: floorPlanId,
          room_id: roomId,
          wall_index: wall.index,
          wall_identifier: wall.identifier,
          wall_length: wallLength,
          wall_height: parseFloat(height),
          unit: unit,
          name: displayName,
          created_by_clerk_id: clerkUserId || null
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      toast.success('Elevation created successfully');
      onElevationCreated(elevation.id);
      onOpenChange(false);
      
      // Navigate to the elevation editor
      navigate(`/elevation/${elevation.id}`);
      
    } catch (error: any) {
      logger.error('Error creating elevation:', error);
      toast.error(error.message || 'Failed to create elevation');
    } finally {
      setIsCreating(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Maximize2 className="h-5 w-5 text-primary" />
            <DialogTitle>Create Elevation View</DialogTitle>
          </div>
          <DialogDescription>
            Configure the elevation view for the selected wall
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          {/* Wall Information */}
          <div className="grid grid-cols-2 gap-4 p-3 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Room</p>
              <p className="font-medium">{roomName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Wall Length</p>
              <p className="font-medium">
                {wall ? formatMeasurement(wall.length, scaleData) : '-'}
              </p>
            </div>
          </div>
          
          {/* Wall Height Input */}
          <div className="space-y-2">
            <Label htmlFor="height">
              Wall Height <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="height"
                type="number"
                step="0.1"
                min="0.1"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="Enter height"
                className="flex-1"
              />
              <div className="flex items-center px-3 border rounded-md bg-muted">
                <span className="text-sm font-medium">{unitAbbr}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Standard ceiling height is typically {isImperial ? '8-10 feet' : '2.4-3.0 meters'}
            </p>
          </div>
          
          {/* Elevation Name (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Elevation Name (Optional)
            </Label>
            <Input
              id="name"
              type="text"
              value={elevationName}
              onChange={(e) => setElevationName(e.target.value)}
              placeholder={defaultName}
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use default: {defaultName}
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!height || parseFloat(height) <= 0 || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Maximize2 className="mr-2 h-4 w-4" />
                Create Elevation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
